<template>
  <div class="mx-3 md:mx-0 bg-white py-4 rounded-lg mt-6 mb-20">
    <!-- Header -->
    <div class="flex justify-between items-center pb-4 px-5 border-b border-b-gray-300 mb-4">
      <h3 class="text-xl text-blue font-semibold">{{ $t('leaderboard') }}</h3>
    </div>
    <div class="my-3 px-3">
      <p class="text-sm text-gray-900 font-medium">{{ $t('leaderboard_info') }}</p>
    </div>
    <!-- Your Rank -->
    <div v-if="userIsContestant" class="border border-gray-300 rounded-lg py-3 mt-6 bg-[#20B3811A] mx-3">
      <div class="flex flex-col items-center px-3 pb-4 border-b border-b-gray-300">
        <h3 class="text-blue text-lg font-semibold mb-2">{{ $t('your_rank') }}</h3>
        <div class="rounded-lg border border-blue bg-white text-center py-2 w-full">
          <p class="text-xl text-gray-900 font-semibold text-center">
            {{ $rankFormat(currentUserData.current_rank) }}
          </p>
        </div>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('current_equity') }}</p>
        <p class="text-blue text-base font-medium">${{ currentUserData.equity }}</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('current_balance') }}</p>
        <p class="text-blue text-base font-medium">${{ currentUserData.balance }}</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('starting_balance') }}</p>
        <p class="text-blue text-base font-medium">$50</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('return_percentage') }}</p>
        <p class="text-blue text-base font-medium">{{ currentUserData.percentage_gain }}%</p>
      </div>
      <div class="px-3 flex justify-between py-3 border-b border-b-gray-300">
        <p class="text-sm text-gray-dark font-medium">{{ $t('win_rate') }}</p>
        <p class="text-blue text-base font-medium">{{ currentUserData.win_rate }}%</p>
      </div>
      <div class="text-center mt-6 mb-2">
        <button @click="openGraph" class="bg-green text-white text-sm font-semibold px-12 rounded-lg py-2">
          {{ $t('view_graph') }}
        </button>
      </div>
    </div>
    <!-- Contestants table -->
    <div class="mb-4 mt-5 mx-3">
      <div class="relative w-full">
        <input
          v-model="nameSearch"
          name="nameSearch"
          class="rounded-md bg-gray-100 ltr:pl-5 rtl:pr-5 w-full h-[50px] text-base text-gray-900 focus:outline-none focus:ring-0 focus:bg-gray-100 border placeholder:text-gray-900 placeholder:text-opacity-20 pr-12"
          type="text"
          :placeholder="$t('enter_full_name').tTitle()"
          @change="$emit('nameChange', nameSearch)"
        />
        <div class="p-3 absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer" @click="$emit('nameChange', nameSearch)">
          <img :src="require('../../../assets/img/icons/competitions/search-icon.svg')" width="16" height="17" alt="Search Icon" />
        </div>
      </div>
    </div>
    <!-- Contestants list -->
    <div class="w-full" v-if="!contestants">
      <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
    </div>
    <div v-else class="w-full text-body p-2 mt-4">
      <div
        v-for="(contestant, i) in allContestants"
        :key="contestant.current_rank"
        class="px-4 pt-[13px] rounded-lg odd:bg-gray-400 overflow-hidden"
        :class="{ 'max-h-[95px]': !contestant.expanded }"
      >
        <div class="flex justify-between items-center" @click="expandStat(i)">
          <div class="text-blue font-bold">
            <label class="block h-9">
              <img
                width="12px"
                height="12px"
                class="ltr:mr-1 rtl:ml-1 cursor-pointer inline"
                :class="{ '-rotate-90': !contestant.expanded }"
                src="../../../assets/img/platform-dropdown.svg"
              />
              {{ $t('rank').tTitle() }}
            </label>
          </div>
          <div class="text-right">
            <p class="font-semibold" :class="{ truncate: !contestant.expanded }">{{ $rankFormat(contestant.current_rank) }}</p>
          </div>
        </div>
        <div class="flex justify-between items-center">
          <div class="text-blue font-bold">
            <label class="block h-9">{{ $t('full_name').tTitle() }}</label>
          </div>
          <div class="text-right">
            <p class="font-semibold h-12">{{ contestant.username }}</p>
          </div>
        </div>
        <div class="flex justify-between items-center">
          <div class="text-blue font-bold">
            <label class="block h-9">{{ $t('country').tTitle() }}</label>
          </div>
          <div class="flex justify-end w-[65%]">
            <img v-if="getFlag(contestant.country_code) !== ''" class="rounded-full  w-[25px] h-[25px]" :src="getFlag(contestant.country_code)" alt="" />
          </div>
        </div>
        <div class="flex justify-between items-center">
          <div class="text-blue font-bold">
            <label class="block h-9">{{ $t('active_days_nr').tTitle() }}</label>
          </div>
          <div class="text-right">
            <p class="font-semibold h-9">{{ contestant.active_days }}</p>
          </div>
        </div>
        <div class="flex justify-between items-center">
          <div class="text-blue font-bold">
            <label class="block h-9">{{ $t('percentage_gain').tTitle() }}</label>
          </div>
          <div class="text-right">
            <p class="font-semibold h-9">{{ contestant.percentage_gain }}</p>
          </div>
        </div>
        <!-- Contestant stats -->
        <div class="flex flex-col gap-y-2 mt-2 mb-3">
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('current_equity') }}</p>
            <p class="text-blue text-base font-medium">${{ contestant.equity }}</p>
          </div>
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('current_balance') }}</p>
            <p class="text-blue text-base font-medium">${{ contestant.balance }}</p>
          </div>
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('starting_balance') }}</p>
            <p class="text-blue text-base font-medium">$50</p>
          </div>
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('return_percentage') }}</p>
            <p class="text-blue text-base font-medium">{{ contestant.percentage_gain }}%</p>
          </div>
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('highest_rank') }}</p>
            <p class="text-blue text-base font-medium">{{ contestant.highest_rank }}</p>
          </div>
          <div class="flex justify-between rounded-lg border border-gray-300 bg-white px-4 py-3">
            <p class="text-sm text-gray-dark font-medium">{{ $t('win_rate') }}</p>
            <p class="text-blue text-base font-medium">{{ contestant.win_rate }}%</p>
          </div>
        </div>
        <!-- Contestant graph -->
        <div class="bg-white rounded-lg border border-gray-300 mb-4">
          <div class="flex justify-end items-center py-3 px-4">
            <div class="flex items-center gap-x-4">
              <div class="flex items-center gap-x-2">
                <div class="rounded-full w-4 h-4 bg-green"></div>
                <p class="text-sm text-gray-900">{{ $t('balance').tTitle() }}</p>
              </div>
              <div class="flex items-center gap-x-2">
                <div class="rounded-full w-4 h-4 bg-blue"></div>
                <p class="text-sm text-gray-900">{{ $t('equity').tTitle() }}</p>
              </div>
            </div>
          </div>
          <div class="px-2">
            <apexchart v-if="Object.keys(graphOptions).length !== 0" width="100%" type="area" :options="graphOptions.chartOptions" :series="graphOptions.series"> </apexchart>
            <div class="w-full" v-else>
              <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
            </div>
          </div>
        </div>
      </div>
      <!-- Pagination -->
      <div v-if="records > 0" class="flex flex-col items-center w-full pt-5 mx-5">
        <div v-if="records > 10" class="flex h-[40px] font-body text-center leading-10">
          <pagination-component :page="page" :maxPerPage="10" :total="records" :showNavOnMobile="true" />
        </div>
        <div class="w-full h-[40px] text-center font-body text-sm leading-10">{{ records }} {{ $t('records') }}</div>
      </div>
    </div>
    <Modal :opened="openUserGraph" @close="openUserGraph = false" customClasses="px-4 pt-6 py-2">
      <div>
        <div class="flex justify-between items-center mb-2">
          <h3 class="text-lg text-blue font-semibold">{{ currentUserData.username }}</h3>
          <div class="flex items-center gap-x-4">
            <div class="flex items-center gap-x-2">
              <div class="rounded-full w-4 h-4 bg-green"></div>
              <p class="text-sm text-gray-900">{{ $t('balance').tTitle() }}</p>
            </div>
            <div class="flex items-center gap-x-2">
              <div class="rounded-full w-4 h-4 bg-blue"></div>
              <p class="text-sm text-gray-900">{{ $t('equity').tTitle() }}</p>
            </div>
          </div>
        </div>
        <apexchart
          v-if="Object.keys(currentUserGraphOptions).length !== 0"
          width="100%"
          type="area"
          :options="currentUserGraphOptions.chartOptions"
          :series="currentUserGraphOptions.series"
        >
        </apexchart>
        <div class="w-full" v-else>
          <h1 class="p-4 text-center text-base">{{ $t('no_records_to_show') }}</h1>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PaginationComponent from '../../../components/Pagination.vue'
import Modal from '@/components/reusableComponents/Modal.vue'
import { service } from '@/main'
import config from '@/config';

export default {
  props: {
    contestants: Array,
    userIsContestant: Boolean,
    page: Number,
    currentUserData: Object,
    selectedContestant: Object,
    records: Number,
  },
  data() {
    return {
      nameSearch: '',
      openUserGraph: false,
      graphOptions: {},
      currentUserGraphOptions: {},
      allContestants: this.contestants,
    }
  },
  components: { Modal, PaginationComponent },
  created() {
    this.$bus.$on('pageChanged', p => {
      this.$emit('pageChange', p)
    })
  },
  mounted() {},
  watch: {
    contestants() {
      this.allContestants = this.contestants
    },
  },
  computed: {},
  methods: {
    async getContestantChart(user) {
      this['logoLoader/set'](true)
      user ? (this.currentUserGraphOptions = {}) : (this.graphOptions = {})
      setTimeout(async () => {
        try {
          const res = await service.post('/trading-competition/graph', {
            rank: user ? user.current_rank : this.selectedContestant.current_rank,
            competition_id: user ? user.competition_id : this.selectedContestant.competition_id,
          })
          if (res.data) {
            let data = res.data.data
            let equity = []
            let balance = []
            let xaxis = []
            if (data) {
              data.map(d => equity.push(d?.equity))
              data.map(d => balance.push(d?.balance))
              data.map(d => xaxis.push(d?.date))
              if (user) {
                this.currentUserGraphOptions = {
                  series: [
                    {
                      name: this.$t('equity').tTitle(),
                      type: 'line', // Balance as a line
                      data: equity,
                      color: '#2B63E3',
                    },
                    {
                      name: this.$t('balance').tTitle(),
                      type: 'area', // Equity as an area
                      data: balance,
                      color: '#20B381',
                    },
                  ],
                  chartOptions: {
                    chart: {
                      height: 350,
                      type: 'line',
                    },
                    legend: {
                      show: false,
                    },
                    dataLabels: {
                      enabled: false,
                    },
                    stroke: {
                      curve: 'straight',
                      width: [3, 2],
                    },
                    fill: {
                      type: ['solid', 'gradient'],
                      opacity: [1, 1], // Full opacity for line, partial for area fill
                    },
                    xaxis: {
                      type: 'datetime',
                      categories: xaxis,
                    },
                    tooltip: {
                      x: {
                        format: 'dd/MMM/yy HH:mm',
                      },
                    },
                  },
                }
              } else
                this.graphOptions = {
                  series: [
                    {
                      name: this.$t('equity').tTitle(),
                      type: 'line', // Balance as a line
                      data: equity,
                      color: '#2B63E3',
                    },
                    {
                      name: this.$t('balance').tTitle(),
                      type: 'area', // Equity as an area
                      data: balance,
                      color: '#20B381',
                    },
                  ],
                  chartOptions: {
                    chart: {
                      height: 350,
                      type: 'line',
                    },
                    legend: {
                      show: false,
                    },
                    dataLabels: {
                      enabled: false,
                    },
                    stroke: {
                      curve: 'straight',
                      width: [3, 2],
                    },
                    fill: {
                      type: ['solid', 'gradient'],
                      opacity: [1, 1], // Full opacity for line, partial for area fill
                    },
                    xaxis: {
                      type: 'datetime',
                      categories: xaxis,
                    },
                    tooltip: {
                      x: {
                        format: 'dd/MMM/yy HH:mm',
                      },
                    },
                  },
                }
            }
            this['logoLoader/set'](false)
          }
        } catch (error) {
          console.log(error)
          this['logoLoader/set'](false)
        }
      }, 200)
    },
    getFlag(country) {
      if(!config.eligibleCountriesTradingCompetition.includes(country)) {
        return require(`../../../assets/img/icons/flags/${country.toLowerCase()}.png`)
      } else return ''
    },
    selectContestant(cont) {
      this.$emit('selectContestant', cont)
    },
    openGraph() {
      this.getContestantChart(this.currentUserData)
      this.openUserGraph = true
    },
    expandStat(index) {
      this.$set(this.allContestants, index, { ...this.allContestants[index], expanded: !this.allContestants[index].expanded })

      // If the current contestant is expanded, execute additional methods
      if (this.allContestants[index].expanded) {
        this.selectContestant(this.allContestants[index])
        this.getContestantChart()
      }
    },
    ...mapActions(['logoLoader/set']),
  },
}
</script>
<style scoped>
.h-auto {
  height: auto;
}

.h-\[90px\] {
  height: 90px;
}
</style>
